import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Check, Circle, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SUBJECTS = ["Math", "Sciences", "English", "Language B", "Individuals & Societies", "Design", "Arts", "PHE", "General"];
const PRIORITY = { high: "text-red-600 bg-red-50 border-red-200", medium: "text-amber-600 bg-amber-50 border-amber-200", low: "text-emerald-600 bg-emerald-50 border-emerald-200" };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("General");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("medium");

  const load = () => api.get("/tasks").then((r) => setTasks(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    await api.post("/tasks", { title, subject, due_date: due || null, priority });
    setTitle(""); setDue(""); setPriority("medium");
    toast.success("Task added");
    load();
  };

  const toggle = async (t) => {
    await api.put(`/tasks/${t.id}`, { completed: !t.completed });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/tasks/${id}`);
    toast.success("Task removed");
    load();
  };

  const pending = tasks.filter((t) => !t.completed);
  const done = tasks.filter((t) => t.completed);

  return (
    <div className="space-y-6">
      <form onSubmit={add} className="bg-white rounded-2xl border border-slate-200 p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="task-form">
        <Input data-testid="task-title-input" placeholder="What needs doing? (e.g. Physics worksheet)" value={title} onChange={(e) => setTitle(e.target.value)} className="lg:col-span-2 rounded-xl" />
        <Select value={subject} onValueChange={setSubject}>
          <SelectTrigger data-testid="task-subject-select" className="rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-2">
          <Input data-testid="task-due-input" type="date" value={due} onChange={(e) => setDue(e.target.value)} className="rounded-xl" />
        </div>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger data-testid="task-priority-select" className="rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="high">High priority</SelectItem>
            <SelectItem value="medium">Medium priority</SelectItem>
            <SelectItem value="low">Low priority</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="task-add-btn" type="submit" className="rounded-xl bg-indigo-600 hover:bg-indigo-700 lg:col-span-3">
          <Plus className="w-4 h-4 mr-1" /> Add task
        </Button>
      </form>

      <div>
        <h3 className="font-heading font-semibold text-slate-900 mb-3">Pending ({pending.length})</h3>
        <div className="space-y-2" data-testid="pending-tasks">
          <AnimatePresence>
            {pending.length === 0 && <p className="text-sm text-slate-400">All caught up! 🎉</p>}
            {pending.map((t) => (
              <TaskRow key={t.id} t={t} toggle={toggle} remove={remove} />
            ))}
          </AnimatePresence>
        </div>
      </div>

      {done.length > 0 && (
        <div>
          <h3 className="font-heading font-semibold text-slate-500 mb-3">Completed ({done.length})</h3>
          <div className="space-y-2">
            {done.map((t) => <TaskRow key={t.id} t={t} toggle={toggle} remove={remove} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function TaskRow({ t, toggle, remove }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}
      className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-3.5"
    >
      <button data-testid={`task-toggle-${t.id}`} onClick={() => toggle(t)} className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors ${t.completed ? "bg-emerald-500 border-emerald-500" : "border-slate-300 hover:border-indigo-400"}`}>
        {t.completed ? <Check className="w-3.5 h-3.5 text-white" /> : <Circle className="w-0 h-0" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${t.completed ? "line-through text-slate-400" : "text-slate-900"}`}>{t.title}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{t.subject}</span>
          {t.due_date && <span className="text-xs text-slate-400">Due {t.due_date}</span>}
          {!t.completed && <span className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1 ${PRIORITY[t.priority]}`}><Flag className="w-2.5 h-2.5" />{t.priority}</span>}
        </div>
      </div>
      <button data-testid={`task-delete-${t.id}`} onClick={() => remove(t.id)} className="text-slate-300 hover:text-red-500 transition-colors">
        <Trash2 className="w-4 h-4" />
      </button>
    </motion.div>
  );
}
